import ErrorLabel from "@/components/ErrorLabel/ErrorLabel";
import LabelWithToolTip from "@/components/LabelWithTooltip/LabelWithToolTip";
import { useMetadataFields } from "@/components/MetadataFields/hooks";
import Checkbox from "@ingka/checkbox";
import { useMemo } from "react";
import {
  FieldAccessContext,
  isLockedForNonIngkaUser,
  isLockedForOwner,
  isUploadFinalized,
  isSocialMediaOnly,
} from "@/components/MetadataFields/formFieldsAccessRules";

type Props = {
  channels: string[];
  fieldAccessContext: FieldAccessContext;
};

const Channels = ({ channels, fieldAccessContext }: Props) => {
  const {
    getValidationError,
    activeAsset,
    updateMetadata,
    isMetadataUpdating,
  } = useMetadataFields();
  const error = getValidationError("channel", activeAsset.id!);
  const selectedCheckboxes = useMemo(
    () => activeAsset.channel || [],
    [activeAsset.channel],
  );
  const { relatedAssetMetadata } = activeAsset;
  const isOnlySocialMedia =
    fieldAccessContext.isIngkaUser &&
    (relatedAssetMetadata?.isOnlySocialMedia || false);
  const ctx = { ...fieldAccessContext, isOnlySocialMedia };
  const isDisabled =
    isLockedForOwner(fieldAccessContext) ||
    isUploadFinalized(ctx) ||
    isLockedForNonIngkaUser(ctx) ||
    isSocialMediaOnly(ctx) ||
    isMetadataUpdating(activeAsset.id!, "channel");

  const toolTipText = isOnlySocialMedia
    ? `If this selection is disabled that is because "For social media only" was selected for this asset in the previous step.`
    : "This will inform other users how they are allowed to use the asset(s) as well as practically impact how you are able to use the asset(s). Some rules regarding accessibility also differs depending on where the assets(s) are used.";

  return (
    <>
      <div className="mb-space150 flex flex-col">
        <LabelWithToolTip
          labelText={"Where is this asset allowed to be used?"}
          toolTipText={toolTipText}
        />
        {Boolean(error) && (
          <ErrorLabel title={`${error?.message}. Select atleast one`} />
        )}
      </div>
      <ul className="space-y-space100">
        {channels.map((item) => (
          <li key={item}>
            <Checkbox
              id={item}
              label={item}
              name={item}
              value={item}
              checked={selectedCheckboxes.includes(item)}
              onChange={(e) => {
                const { value } = e.target;
                updateMetadata({
                  channel: selectedCheckboxes.includes(value)
                    ? selectedCheckboxes.filter((item) => item !== value)
                    : [...selectedCheckboxes, value],
                });
              }}
              disabled={isDisabled}
            />
          </li>
        ))}
      </ul>
    </>
  );
};

export default Channels;
